import type { ReplySnapshot } from './undoSend';

/**
 * Held replies remembered in this browser so the countdown and Undo survive a
 * reload, or closing and reopening the ticket, while the window is still open.
 * Only a hint: on load every entry is checked with the server, which decides.
 */
export interface StoredPendingSend {
  operationId: string;
  /** Local-clock time the email goes out. */
  deadline: number;
  undoSeconds: number;
  recipientsLabel: string;
  snapshot: ReplySnapshot;
  startedAt: number;
}

const PREFIX = 'undoSend:v1:';
// Well past the longest window (60s) plus the gateway's reconcile delay.
export const STORED_MAX_AGE_MS = 15 * 60_000;
const MAX_PER_KEY = 20;

function storage(): Storage | null {
  try {
    return typeof window !== 'undefined' ? window.localStorage : null;
  } catch {
    return null;
  }
}

function isEntry(value: unknown): value is StoredPendingSend {
  const v = value as StoredPendingSend;
  return (
    !!v &&
    typeof v.operationId === 'string' &&
    typeof v.deadline === 'number' &&
    typeof v.startedAt === 'number' &&
    typeof v.undoSeconds === 'number' &&
    typeof v.recipientsLabel === 'string' &&
    !!v.snapshot &&
    typeof v.snapshot.content === 'string' &&
    Array.isArray(v.snapshot.attachments)
  );
}

function fresh(entry: StoredPendingSend, now: number): boolean {
  return now - entry.startedAt < STORED_MAX_AGE_MS && entry.startedAt <= now + 60_000;
}

function readKey(store: Storage, key: string): StoredPendingSend[] {
  try {
    const parsed = JSON.parse(store.getItem(key) ?? '[]');
    return Array.isArray(parsed) ? parsed.filter(isEntry) : [];
  } catch {
    return [];
  }
}

function writeKey(store: Storage, key: string, entries: StoredPendingSend[]): void {
  try {
    if (entries.length === 0) store.removeItem(key);
    else store.setItem(key, JSON.stringify(entries.slice(-MAX_PER_KEY)));
  } catch {
    // Full or blocked storage: Undo just won't survive a reload.
  }
}

/** Drop expired entries left behind by any ticket (closed tabs, crashes). */
function prune(store: Storage, now: number): void {
  try {
    const keys: string[] = [];
    for (let i = 0; i < store.length; i++) {
      const key = store.key(i);
      if (key?.startsWith(PREFIX)) keys.push(key);
    }
    for (const key of keys) {
      const entries = readKey(store, key);
      const kept = entries.filter((e) => fresh(e, now));
      if (kept.length !== entries.length || kept.length === 0) writeKey(store, key, kept);
    }
  } catch {
    // Ignore.
  }
}

/** The localStorage key holding one user + ticket's entries (for storage events). */
export function pendingSendsStorageKey(key: string): string {
  return PREFIX + key;
}

export function loadPendingSends(key: string, now = Date.now()): StoredPendingSend[] {
  const store = storage();
  if (!store) return [];
  prune(store, now);
  return readKey(store, PREFIX + key).filter((e) => fresh(e, now));
}

export function savePendingSend(key: string, entry: StoredPendingSend): void {
  const store = storage();
  if (!store) return;
  const others = readKey(store, PREFIX + key).filter((e) => e.operationId !== entry.operationId);
  writeKey(store, PREFIX + key, [...others, entry]);
}

export function forgetPendingSend(key: string, operationId: string): void {
  const store = storage();
  if (!store) return;
  const entries = readKey(store, PREFIX + key);
  const kept = entries.filter((e) => e.operationId !== operationId);
  if (kept.length !== entries.length) writeKey(store, PREFIX + key, kept);
}
